import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { streamSse } from "@/api/sseClient";
import type { AnalysisSessionDetail } from "@/api/types";

/**
 * Hook que maneja el streaming en vivo del análisis profundo (F14).
 *
 * Cada turno (kickoff o mensaje del operador) abre una conexión SSE
 * contra POST /sessions/{id}/messages/stream. Mantiene estado reactivo:
 *
 *   - liveTokens: texto del último mensaje del modelo que sigue
 *     siendo redactado letra por letra (se mueve a messages[] cuando
 *     llega `complete`).
 *   - liveToolCalls: tool calls del turno actual con su estado
 *     ("running" o "done") + duración cuando termina.
 *   - status: "idle" | "streaming" | "error".
 *
 * Cuando el stream cierra (event `complete` o error), invalidamos la
 * query del session detail para refrescar el snapshot consistente desde
 * DB (incluye los mensajes completos y tokens acumulados).
 */

export type LiveToolCallState = "running" | "done";

export interface LiveToolCall {
  tool_use_id: string;
  tool_name: string;
  tool_input_summary: string;
  state: LiveToolCallState;
  duration_ms: number | null;
  ok: boolean | null;
}

export type StreamStatus = "idle" | "streaming" | "error";

export interface UseAnalysisStreamResult {
  status: StreamStatus;
  liveTokens: string;
  liveToolCalls: LiveToolCall[];
  errorMessage: string | null;
  iteration: number;
  kickoff: () => void;
  sendMessage: (content: string) => void;
  cancel: () => void;
  reset: () => void;
}

interface KickoffEvent {
  iteration?: number;
}
interface ToolStartedEvent {
  tool_name?: string;
  tool_use_id?: string;
  tool_input_summary?: string;
}
interface ToolCompletedEvent {
  tool_name?: string;
  tool_use_id?: string;
  duration_ms?: number;
  ok?: boolean;
}
interface TokenEvent {
  text?: string;
  index?: number;
}
interface CompleteEvent {
  session_id?: string;
  total_tokens?: number;
}
interface ErrorEvent {
  code?: string;
  message?: string;
}

export function useAnalysisStream(
  sessionId: string | undefined,
): UseAnalysisStreamResult {
  const qc = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>("idle");
  const [liveTokens, setLiveTokens] = useState("");
  const [liveToolCalls, setLiveToolCalls] = useState<LiveToolCall[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [iteration, setIteration] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setLiveTokens("");
    setLiveToolCalls([]);
    setErrorMessage(null);
    setIteration(0);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus("idle");
  }, []);

  // Limpieza al desmontar o cambiar de sesión.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [sessionId]);

  const openStream = useCallback(
    (body: { type: "kickoff" | "message"; content?: string }) => {
      if (!sessionId) return;
      // Si ya hay un stream activo, lo cancelamos antes de abrir uno nuevo.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      reset();
      setStatus("streaming");

      const handleEvent = (event: string, data: unknown) => {
        const payload = (data ?? {}) as Record<string, unknown>;
        switch (event) {
          case "iteration_started": {
            const e = payload as KickoffEvent;
            setIteration(e.iteration ?? 0);
            // Nueva iteración del loop: limpiamos tokens en vivo (los
            // anteriores ya quedaron persistidos como mensaje completo).
            setLiveTokens("");
            break;
          }
          case "tool_call_started": {
            const e = payload as ToolStartedEvent;
            setLiveToolCalls((prev) => [
              ...prev,
              {
                tool_use_id: e.tool_use_id ?? "",
                tool_name: e.tool_name ?? "",
                tool_input_summary: e.tool_input_summary ?? "",
                state: "running",
                duration_ms: null,
                ok: null,
              },
            ]);
            break;
          }
          case "tool_call_completed": {
            const e = payload as ToolCompletedEvent;
            setLiveToolCalls((prev) =>
              prev.map((tc) =>
                tc.tool_use_id === e.tool_use_id
                  ? {
                      ...tc,
                      state: "done" as const,
                      duration_ms: e.duration_ms ?? null,
                      ok: e.ok ?? null,
                    }
                  : tc,
              ),
            );
            break;
          }
          case "llm_token": {
            const e = payload as TokenEvent;
            setLiveTokens((prev) => prev + (e.text ?? ""));
            break;
          }
          case "complete": {
            const _e = payload as CompleteEvent;
            void _e;
            setStatus("idle");
            // El snapshot consistente con todos los mensajes finales
            // vive en DB; refrescamos la query para que la UI use ese
            // estado autoritativo y limpie liveTokens/liveToolCalls.
            qc.invalidateQueries({
              queryKey: ["analysis", "session", sessionId],
            });
            // No reseteamos aquí: la UI puede preferir mantener el
            // último liveTokens durante el fade-out. Reset explícito
            // viene en reset() o al abrir el próximo stream.
            break;
          }
          case "error": {
            const e = payload as ErrorEvent;
            setErrorMessage(e.message ?? e.code ?? "Error desconocido");
            setStatus("error");
            qc.invalidateQueries({
              queryKey: ["analysis", "session", sessionId],
            });
            break;
          }
          default:
            // Eventos desconocidos: ignorar silenciosamente (forward
            // compat con eventos nuevos que el back agregue).
            break;
        }
      };

      void streamSse({
        url: `/api/v1/analysis/sessions/${sessionId}/messages/stream`,
        body,
        signal: controller.signal,
        onEvent: handleEvent,
        onError: (err) => {
          if (controller.signal.aborted) return;
          setErrorMessage(err.message);
          setStatus("error");
          qc.invalidateQueries({
            queryKey: ["analysis", "session", sessionId],
          });
        },
      });
    },
    [qc, reset, sessionId],
  );

  const kickoff = useCallback(() => {
    openStream({ type: "kickoff" });
  }, [openStream]);

  const sendMessage = useCallback(
    (content: string) => {
      const text = content.trim();
      if (!text) return;
      openStream({ type: "message", content: text });
    },
    [openStream],
  );

  return {
    status,
    liveTokens,
    liveToolCalls,
    errorMessage,
    iteration,
    kickoff,
    sendMessage,
    cancel,
    reset,
  };
}

/**
 * Helper para arrancar una sesión con `defer_first_turn=true`. El flujo
 * F14 separa la creación de la sesión (rápida, sync) del primer turno
 * (lento, se ve por stream).
 */
export async function startAnalysisDeferred(params: {
  incident_id?: string | null;
  trigger_type: "manual" | "proactive" | "automatic";
  target_host?: string | null;
}): Promise<AnalysisSessionDetail> {
  return api<AnalysisSessionDetail>("/api/v1/analysis/start", {
    method: "POST",
    body: { ...params, defer_first_turn: true },
  });
}
